import axios, { HttpStatusCode } from "axios";
import eventEmitter from "./eventEmitter";
import localStorageService from "../services/localStorageService";

const api = axios.create({
  // baseURL: "https://your-api.com", // Change this to your API base URL
  headers: {
    "Content-Type": "application/json",
  },
});

type TrialBlockDetail = {
  code?: string;
  message?: string;
};

// Request Interceptor: add auth token (used by GameDashboard/PlayerDashboard, etc.)
api.interceptors.request.use((config) => {
  const userInfo = localStorageService.getUserInfo?.();
  const token = userInfo?.access_token || userInfo?.token || "";
  if (!token) return config;

  const headers: any = config.headers ?? {};
  const hasAuthHeader =
    headers.Authorization ||
    headers.authorization ||
    (typeof headers.get === "function" && (headers.get("Authorization") || headers.get("authorization")));

  if (!hasAuthHeader) {
    if (typeof headers.set === "function") {
      headers.set("Authorization", `Bearer ${token}`);
    } else {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  config.headers = headers;
  return config;
});

// Response Interceptor (Handle Unauthorized Errors)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail: TrialBlockDetail | undefined = error?.response?.data?.detail;

    if (status === HttpStatusCode.Forbidden && detail && typeof detail === "object") {
      if (detail.code === "TRIAL_EXPIRED") {
        eventEmitter.emit("trial_expired", {
          code: detail.code,
          message:
            detail.message ||
            "Your trial has ended . Please upgrade to continue accessing our platform",
        });
      }
      if (detail.code === "SUBSCRIPTION_EXPIRED") {
        // Treat as locked/paid-subscription problem; do not open the trial-expired modal.
        eventEmitter.emit("subscription_expired", {
          code: detail.code,
          message: detail.message || "Subscription required to access this sport.",
        });
      }
      if (detail.code === "TEAM_SEAT_OVERFLOW") {
        eventEmitter.emit("team_seat_overflow", {
          code: detail.code,
          message:
            detail.message ||
            "Access is currently unavailable because your team has reached the maximum number of allowed users. Please contact your team administrator to request access.",
        });
      }
    }

    if (status === HttpStatusCode.Unauthorized) {
      // Avoid redirect loops on public/unauthenticated requests.
      // Only emit unauthorized if this request was authenticated (had a token/header).
      const userInfo = localStorageService.getUserInfo?.();
      const token = userInfo?.access_token || userInfo?.token;
      const headers: any = error?.config?.headers ?? {};
      const hadAuthHeader =
        headers.Authorization ||
        headers.authorization ||
        (typeof headers.get === "function" && (headers.get("Authorization") || headers.get("authorization")));

      if (hadAuthHeader || token) eventEmitter.emit("unauthorized");
    }
    return Promise.reject(error);
  }
);

export default api;
